import { parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import useMoney from '../../../../../../../../../hooks/useMoney';
import LangEnum from '../../../../../../../../../lang/LangEnum';
import { useFindOrderConditionFreightQuotes } from '../../../../../../../../../services/modules/orderCondition/orderCondition.queries';
import { OrderConditionFreightQuote } from '../../../../../../../../../services/modules/orderCondition/orderCondition.types';
import snackbarStore from '../../../../../../../../../stores/Snackbar.store';
import { useTranslation } from '../../../../../../../../translation/TranslationContext';
import {
  OrderConditionFreightFieldProps,
  OrderConditionFreightOption,
  OrderConditionSavedFreight,
} from '../orderConditionFreightField.types';
import { isQuotedFreight, isSavedQuoteOption } from '../orderConditionFreightField.utils';
import useFreightQuoteError, { isUnavailableIntegrationError } from './useFreightQuoteError';

const isQuotable = (quote: OrderConditionFreightQuote) => quote.validation?.isValid !== false;

const buildQuoteOptionId = (quote: OrderConditionFreightQuote, index: number) =>
  [
    index,
    quote.shippingCompany?.name ?? '',
    quote.shippingMethod.externalId ?? '',
    quote.shippingMethod.name,
  ].join('|');

const useOrderConditionFreightField = ({
  orderId,
  orderConditionId,
  savedFreight,
  isDisabled,
  canEnterManualFreight,
  hasError,
  hasPrefetchedQuotes = false,
  onBlur,
  onSelect,
  onRequestManualEntry,
}: OrderConditionFreightFieldProps) => {
  const { translate } = useTranslation();
  const { formatMoney } = useMoney();
  const { getErrorMessage } = useFreightQuoteError();
  const { handleMessage } = snackbarStore;
  const [hasQuoted, setHasQuoted] = useState(hasPrefetchedQuotes);
  const [shouldReuseQuotesOnOpen, setShouldReuseQuotesOnOpen] = useState(hasPrefetchedQuotes);

  const quotesQuery = useFindOrderConditionFreightQuotes(
    { orderId, orderConditionId },
    { enabled: hasQuoted },
  );

  const quotes = quotesQuery.data ?? [];

  const formatEstimate = (businessDays?: number | null) =>
    businessDays === null || businessDays === undefined
      ? undefined
      : translate(LangEnum.ORDER_CONDITION_FREIGHT_BUSINESS_DAYS, { quantity: businessDays });

  const toOption = (
    quote: OrderConditionFreightQuote,
    index: number,
  ): OrderConditionFreightOption => ({
    id: buildQuoteOptionId(quote, index),
    companyName: quote.shippingCompany?.name,
    methodName: quote.shippingMethod.name,
    priceLabel: formatMoney(quote.price),
    estimateLabel: formatEstimate(quote.estimatedShippingBusinessDays),
    isValid: isQuotable(quote),
    invalidReason: quote.validation?.reason ?? undefined,
    quoteId: quote.quoteId,
  });

  const buildSelectedLabel = (freight: OrderConditionSavedFreight) => {
    if (!freight.shippingMethod) {
      return undefined;
    }

    const price =
      freight.shippingCostValue === null || freight.shippingCostValue === undefined
        ? undefined
        : formatMoney(freight.shippingCostValue);

    return [
      freight.shippingCompanyName,
      freight.shippingMethod,
      formatEstimate(freight.estimatedShippingBusinessDays),
      price,
    ]
      .filter(Boolean)
      .join(' · ');
  };

  const isUnavailableIntegration =
    quotesQuery.isError && isUnavailableIntegrationError(quotesQuery.error);

  const handleOpen = () => {
    if (!hasQuoted) {
      setHasQuoted(true);
      return;
    }

    if (shouldReuseQuotesOnOpen) {
      setShouldReuseQuotesOnOpen(false);
      return;
    }

    quotesQuery.refetch();
  };

  const handleSelect = (optionId: string) => {
    const quote = quotes.find((item, index) => buildQuoteOptionId(item, index) === optionId);

    if (!quote || !isQuotable(quote)) {
      return;
    }

    onSelect({
      shippingCompanyName: quote.shippingCompany?.name ?? null,
      shippingCompanyExternalId: quote.shippingMethod.externalId ?? null,
      shippingMethod: quote.shippingMethod.name,
      price: quote.price,
      estimatedShippingBusinessDays: quote.estimatedShippingBusinessDays,
      estimatedShippingDate: quote.estimatedShippingDate
        ? parseISO(quote.estimatedShippingDate)
        : null,
      quoteId: quote.quoteId ?? null,
    });
  };

  const errorMessage =
    quotesQuery.isError && !isUnavailableIntegration
      ? getErrorMessage(quotesQuery.error)
      : undefined;
  const selectedLabel = isQuotedFreight(savedFreight)
    ? buildSelectedLabel(savedFreight)
    : undefined;
  const options = quotes.map((quote, index) => toOption(quote, index));
  const selectedOption = options.find((option) => isSavedQuoteOption(option, savedFreight));

  useEffect(() => {
    if (!quotesQuery.isError) {
      return;
    }

    handleMessage({ type: 'error', message: getErrorMessage(quotesQuery.error) });
  }, [quotesQuery.error]);

  return {
    options,
    selectedOptionId: selectedOption?.id ?? '',
    selectedLabel,
    isDisabled,
    isLoading: quotesQuery.isFetching,
    hasError,
    hasQuoteError: quotesQuery.isError,
    canEnterManually: isUnavailableIntegration && canEnterManualFreight,
    errorMessage,
    onBlur,
    onOpen: handleOpen,
    onSelect: handleSelect,
    onRequestManualEntry,
  };
};

export default useOrderConditionFreightField;
